import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, getToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtEuro, fmtDate, isInternal, STATUS_META, isQuinto } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/MultiSelect";
import { Search, Plus, Download, FileText, Paperclip, MessageSquare, RotateCcw, Trash2 } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast } from "sonner";
import { ExportDialog, DATE_FIELDS_PRATICHE, PRACTICE_EXPORT_FIELDS } from "@/components/ExportDialog";
import { useExports } from "@/context/ExportsContext";

const PRODUCTS = ["Mutuo Ipotecario", "Prestito Personale", "Cessione del Quinto", "Leasing Strumentale", "Fido Bancario", "Consolidamento Debiti", "Delega di Pagamento", "Altro"];

export default function Practices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [practices, setPractices] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [statuses, setStatuses] = useState([]);

  const SAVED = (() => { try { return JSON.parse(sessionStorage.getItem("practices-filters") || "{}"); } catch { return {}; } })();
  const [q, setQ] = useState(SAVED.q || "");
  const [statusFilter, setStatusFilter] = useState(SAVED.statusFilter || "all");
  const [ownerFilter, setOwnerFilter] = useState(SAVED.ownerFilter || "all");
  const [convFilter, setConvFilter] = useState(SAVED.convFilter || "all");
  const [collabFilter, setCollabFilter] = useState(SAVED.collabFilter || "all");
  const [segnFilter, setSegnFilter] = useState(SAVED.segnFilter || "all");
  const [dateFrom, setDateFrom] = useState(SAVED.dateFrom || "");
  const [dateTo, setDateTo] = useState(SAVED.dateTo || "");
  const [owners, setOwners] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [products, setProducts] = useState(PRODUCTS);
  const [sedeFilter, setSedeFilter] = useState(SAVED.sedeFilter || "all");
  const [canaleFilter, setCanaleFilter] = useState(SAVED.canaleFilter || "all");
  const [prodottoFilter, setProdottoFilter] = useState(SAVED.prodottoFilter || "all");
  const [showHidden, setShowHidden] = useState(!!SAVED.showHidden);
  const [open, setOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [form, setForm] = useState({ tipo_finanziamento: "", numero_istituto: "", importo: "", cliente_nome: "", cliente_cognome: "", ragione_sociale: "", convenzionato_id: "none", collaboratore_id: "none", canale: "Diretto", soggetto_tipo: "fisica", email: "", telefono: "", anagrafiche_ids: [], sede_id: "none", data_scadenza: "", note_iniziale: "", valore_bene: "", anticipo: "", spese_istruttoria: "", tabella_finanziaria: "", assicurazione_cpi: "", assicurazione_cvt: "", durata: "" });
  const isConv = user?.role === "convenzionato";
  const myCollabName = owners.find((o) => o.id === user?.referente_id)?.name;
  const mySedeName = sedi.find((s) => s.id === user?.sede_id)?.nome;
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const arrow = (key) => (sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "");
  const isAdmin = user?.role === "admin";
  const [sel, setSel] = useState([]);
  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente ${sel.length} pratiche selezionate? L'operazione non è reversibile.`)) return;
    try {
      const { data } = await api.post("/practices/bulk-delete", { ids: sel });
      toast.success(`${data.deleted} pratiche eliminate`); setSel([]); load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const [bulkStatus, setBulkStatus] = useState("");
  const bulkChangeStatus = async () => {
    if (!bulkStatus) { toast.error("Seleziona lo stato"); return; }
    if (!window.confirm(`Impostare lo stato "${bulkStatus}" su ${sel.length} pratiche selezionate?`)) return;
    try {
      const { data } = await api.post("/practices/bulk-status", { ids: sel, status: bulkStatus });
      toast.success(`Stato aggiornato su ${data.updated} pratiche`); setSel([]); setBulkStatus(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento stato"); }
  };

  const liqOf = (p) => p.liquidated_at || [...(p.status_history || [])].reverse().find((h) => h.status === "Liquidata")?.changed_at;
  const saveLiquidation = async (pid, val) => {
    if (!val) return;
    try { await api.put(`/practices/${pid}`, { liquidated_at: val }); toast.success("Data liquidazione aggiornata"); load(); }
    catch { toast.error("Errore nell'aggiornamento"); }
  };
  const paged = practices;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const load = useCallback(() => {
    const params = { page, page_size: pageSize, sort_key: sortKey, sort_dir: sortDir };
    if (q) params.q = q;
    if (statusFilter !== "all") params.status = statusFilter;
    if (ownerFilter !== "all") params.owner = ownerFilter;
    if (convFilter !== "all") params.convenzionato = convFilter;
    if (collabFilter !== "all") params.collaboratore = collabFilter;
    if (segnFilter !== "all") params.segnalatore = segnFilter;
    if (sedeFilter !== "all") params.sede = sedeFilter;
    if (canaleFilter !== "all") params.canale = canaleFilter;
    if (prodottoFilter !== "all") params.prodotto = prodottoFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (showHidden) params.include_hidden = true;
    api.get("/practices", { params }).then((r) => { setPractices(r.data.items || []); setTotal(r.data.total || 0); setStatusCounts(r.data.status_counts || {}); });
  }, [q, statusFilter, ownerFilter, convFilter, collabFilter, segnFilter, sedeFilter, canaleFilter, prodottoFilter, dateFrom, dateTo, showHidden, page, sortKey, sortDir]);

  useEffect(() => { setPage(1); }, [q, statusFilter, ownerFilter, convFilter, collabFilter, segnFilter, sedeFilter, canaleFilter, prodottoFilter, dateFrom, dateTo, showHidden, sortKey, sortDir]);

  useEffect(() => {
    api.get("/meta").then((r) => { setStatuses(r.data.statuses); if (r.data.products) setProducts(r.data.products); });
    api.get("/sedi").then((r) => setSedi(r.data));
    if (isInternal(user?.role) || user?.role === "convenzionato") api.get("/users/partners").then((r) => setOwners(r.data));
  }, [user]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    sessionStorage.setItem("practices-filters", JSON.stringify({ q, statusFilter, ownerFilter, convFilter, collabFilter, segnFilter, sedeFilter, canaleFilter, prodottoFilter, dateFrom, dateTo, showHidden }));
  }, [q, statusFilter, ownerFilter, convFilter, collabFilter, segnFilter, sedeFilter, canaleFilter, prodottoFilter, dateFrom, dateTo, showHidden]);

  useEffect(() => {
    const aid = searchParams.get("anagrafica");
    if (!aid) return;
    setSearchParams({}, { replace: true });
    (async () => {
      try {
        const { data: a } = await api.get(`/anagrafiche/${aid}`);
        const isGiuridica = a.tipo === "giuridica" || !!a.ragione_sociale;
        setForm((f) => ({
          ...f,
          anagrafiche_ids: [aid],
          soggetto_tipo: isGiuridica ? "giuridica" : "fisica",
          cliente_nome: a.nome || "",
          cliente_cognome: a.cognome || "",
          ragione_sociale: a.ragione_sociale || "",
          email: a.email || "",
          telefono: a.cellulare || a.telefono || "",
        }));
      } catch {
        setForm((f) => ({ ...f, anagrafiche_ids: [aid] }));
      }
      setOpen(true);
    })();
  }, [searchParams, setSearchParams]);

  const onConvChange = (v) => {
    const o = owners.find((x) => x.id === v);
    if (!o) { setForm({ ...form, convenzionato_id: v }); return; }
    const collab = o.referente_id && owners.some((x) => x.id === o.referente_id && x.role === "collaboratore") ? o.referente_id : form.collaboratore_id;
    setForm({ ...form, convenzionato_id: v, canale: "Convenzionato", collaboratore_id: collab, sede_id: o.sede_id || form.sede_id });
  };

  const create = async () => {
    if (!form.cliente_nome || !form.cliente_cognome || !form.tipo_finanziamento) { toast.error("Nome, cognome e prodotto sono obbligatori"); return; }
    const missing = [];
    if (!(parseFloat(form.importo) > 0)) missing.push("importo");
    if (isConv ? !user?.sede_id : form.sede_id === "none") missing.push("sede");
    if (!form.email.trim()) missing.push("email");
    if (!form.telefono.trim()) missing.push("cellulare");
    if (missing.length) { toast.error(`Campi obbligatori mancanti: ${missing.join(", ")}`); return; }
    try {
      const { data } = await api.post("/practices", { ...form, importo: form.importo ? parseFloat(form.importo) : 0,
        sede_id: isConv ? (user.sede_id || null) : (form.sede_id === "none" ? null : form.sede_id),
        canale: isConv ? "Convenzionato" : form.canale,
        convenzionato_id: isConv ? user.id : (form.convenzionato_id === "none" ? null : form.convenzionato_id),
        collaboratore_id: isConv ? (user.referente_id || null) : (form.collaboratore_id === "none" ? null : form.collaboratore_id),
        valore_bene: form.valore_bene ? parseFloat(form.valore_bene) : null,
        durata: form.durata ? parseInt(form.durata, 10) : null,
        montante: form.montante ? parseFloat(form.montante) : null,
        anticipo: form.anticipo ? parseFloat(form.anticipo) : null,
        spese_istruttoria: form.spese_istruttoria ? parseFloat(form.spese_istruttoria) : null,
        assicurazione_cpi: form.assicurazione_cpi || null,
        assicurazione_cvt: form.assicurazione_cvt || null,
        tabella_finanziaria: form.tabella_finanziaria || null });
      toast.success(`Pratica ${data.numero} creata`);
      setOpen(false);
      setForm({ tipo_finanziamento: "", numero_istituto: "", importo: "", cliente_nome: "", cliente_cognome: "", ragione_sociale: "", convenzionato_id: "none", collaboratore_id: "none", canale: "Diretto", soggetto_tipo: "fisica", email: "", telefono: "", anagrafiche_ids: [], sede_id: "none", data_scadenza: "", note_iniziale: "", valore_bene: "", anticipo: "", spese_istruttoria: "", tabella_finanziaria: "", assicurazione_cpi: "", assicurazione_cvt: "", durata: "" });
      navigate(`/pratiche/${data.id}`);
    } catch (e) { toast.error("Errore nella creazione"); }
  };

  const { startExport } = useExports();
  const onExport = (params) => startExport("reports_excel", { ...(statusFilter !== "all" ? { status: statusFilter } : {}), ...params }, "Pratiche");

  const anagName = (a) => a.tipo === "giuridica" ? a.ragione_sociale : `${a.nome || ""} ${a.cognome || ""}`.trim();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Pratiche</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} pratiche {isInternal(user?.role) ? "totali" : "tue"}</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" data-testid="practice-export-excel-btn" onClick={() => setExportOpen(true)}>
              <Download className="h-4 w-4 mr-2" /> Excel
            </Button>
          )}
          <ExportDialog open={exportOpen} onOpenChange={setExportOpen} title="Esporta Pratiche (Report)" fields={PRACTICE_EXPORT_FIELDS} dateFields={DATE_FIELDS_PRATICHE} onConfirm={onExport} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-practice-modal-btn"><Plus className="h-4 w-4 mr-2" /> Nuova Pratica</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Nuova Pratica di Finanziamento</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Nome Cliente *</Label>
                    <Input data-testid="new-practice-nome" value={form.cliente_nome} onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })} placeholder="Mario" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Cognome Cliente *</Label>
                    <Input data-testid="new-practice-cognome" value={form.cliente_cognome} onChange={(e) => setForm({ ...form, cliente_cognome: e.target.value })} placeholder="Rossi" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Soggetto</Label>
                  <div className="flex gap-2">
                    {[["fisica", "Persona Fisica"], ["giuridica", "Partita IVA"]].map(([v, lab]) => (
                      <button key={v} type="button" data-testid={`new-practice-soggetto-${v}`} onClick={() => setForm({ ...form, soggetto_tipo: v })}
                        className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${form.soggetto_tipo === v ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"}`}>{lab}</button>
                    ))}
                  </div>
                </div>
                {form.soggetto_tipo === "giuridica" && (
                  <div className="space-y-1.5">
                    <Label>Ragione Sociale *</Label>
                    <Input data-testid="new-practice-ragione" value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} placeholder="es. Rossi Costruzioni Srl" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Prodotto finanziario *</Label>
                    <Select value={form.tipo_finanziamento || undefined} onValueChange={(v) => setForm({ ...form, tipo_finanziamento: v })}>
                      <SelectTrigger data-testid="new-practice-tipo"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Importo (€) *</Label>
                    <Input data-testid="new-practice-importo" type="number" step="0.01" min="0" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} placeholder="50000" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Convenzionato</Label>
                    {isConv ? (
                      <Input data-testid="new-practice-convenzionato" value={user?.name || ""} disabled readOnly />
                    ) : (
                    <SearchableSelect value={form.convenzionato_id} onValueChange={onConvChange}
                      testid="new-practice-convenzionato" placeholder="Seleziona" searchPlaceholder="Cerca convenzionato…"
                      options={[{ value: "none", label: "Nessuno" }, ...owners.filter((o) => o.role === "convenzionato").map((o) => ({ value: o.id, label: o.name }))]} />
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Collaboratore</Label>
                    {isConv ? (
                      <Input data-testid="new-practice-collaboratore" value={myCollabName || "Nessun collaboratore assegnato"} disabled readOnly />
                    ) : (
                    <SearchableSelect value={form.collaboratore_id} onValueChange={(v) => setForm({ ...form, collaboratore_id: v })}
                      testid="new-practice-collaboratore" placeholder="Seleziona" searchPlaceholder="Cerca collaboratore…"
                      options={[{ value: "none", label: "Nessuno" }, ...owners.filter((o) => o.role === "collaboratore").map((o) => ({ value: o.id, label: o.name }))]} />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Canale di provenienza</Label>
                    {isConv ? (
                      <Input data-testid="new-practice-canale" value="Convenzionato" disabled readOnly />
                    ) : (
                    <Select value={form.canale} onValueChange={(v) => setForm({ ...form, canale: v })}>
                      <SelectTrigger data-testid="new-practice-canale"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Convenzionato", "Diretto", "Campagna", "Altro"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Sede di provenienza *</Label>
                    {isConv ? (
                      <Input data-testid="new-practice-sede" value={mySedeName || "Nessuna sede assegnata"} disabled readOnly />
                    ) : (
                    <Select value={form.sede_id} onValueChange={(v) => setForm({ ...form, sede_id: v })}>
                      <SelectTrigger data-testid="new-practice-sede"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessuna sede</SelectItem>
                        {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Email *</Label>
                    <Input data-testid="new-practice-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@email.it" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Cellulare *</Label>
                    <Input data-testid="new-practice-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="333 1234567" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>N. Pratica Istituto (opzionale)</Label>
                  <Input data-testid="new-practice-numero-istituto" value={form.numero_istituto} onChange={(e) => setForm({ ...form, numero_istituto: e.target.value })} placeholder="Es. numero assegnato dall'istituto/banca" />
                </div>
                <div className="space-y-1.5">
                  <Label>Data di scadenza (opzionale)</Label>
                  <Input type="date" data-testid="new-practice-scadenza" value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} />
                </div>
                {isQuinto(form.tipo_finanziamento) && (
                  <div className="space-y-3 rounded-lg border border-border p-3" data-testid="quinto-fields-section">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dettagli {form.tipo_finanziamento}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Montante (€)</Label><Input data-testid="new-practice-montante" type="number" value={form.montante || ""} onChange={(e) => setForm({ ...form, montante: e.target.value })} placeholder="0" /></div>
                      <div className="space-y-1.5"><Label>Durata (mesi)</Label><Input data-testid="new-practice-durata-quinto" type="number" value={form.durata || ""} onChange={(e) => setForm({ ...form, durata: e.target.value })} placeholder="es. 120" /></div>
                    </div>
                  </div>
                )}
                {["Prestito Auto", "Altro Finalizzato", "Fotovoltaico"].includes(form.tipo_finanziamento) && (
                  <div className="space-y-3 rounded-lg border border-border p-3" data-testid="extra-fields-section">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dettagli finanziamento (opzionali)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Valore bene (€)</Label><Input data-testid="new-practice-valore-bene" type="number" value={form.valore_bene} onChange={(e) => setForm({ ...form, valore_bene: e.target.value })} placeholder="0" /></div>
                      <div className="space-y-1.5"><Label>Anticipo (€)</Label><Input data-testid="new-practice-anticipo" type="number" value={form.anticipo} onChange={(e) => setForm({ ...form, anticipo: e.target.value })} placeholder="0" /></div>
                      <div className="space-y-1.5"><Label>Durata (mesi)</Label><Input data-testid="new-practice-durata" type="number" value={form.durata} onChange={(e) => setForm({ ...form, durata: e.target.value })} placeholder="es. 36" /></div>
                      <div className="space-y-1.5"><Label>Spese Istruttoria (€)</Label><Input data-testid="new-practice-spese-istruttoria" type="number" value={form.spese_istruttoria} onChange={(e) => setForm({ ...form, spese_istruttoria: e.target.value })} placeholder="0" /></div>
                      <div className="space-y-1.5"><Label>Tabella finanziaria</Label><Input data-testid="new-practice-tabella-finanziaria" value={form.tabella_finanziaria} onChange={(e) => setForm({ ...form, tabella_finanziaria: e.target.value })} placeholder="es. TAN/TAEG" /></div>
                      <div className="space-y-1.5"><Label>Assicurazione CPI</Label><Input data-testid="new-practice-ass-cpi" value={form.assicurazione_cpi} onChange={(e) => setForm({ ...form, assicurazione_cpi: e.target.value })} placeholder="es. Sì / importo / note" /></div>
                      <div className="space-y-1.5"><Label>Assicurazione CVT</Label><Input data-testid="new-practice-ass-cvt" value={form.assicurazione_cvt} onChange={(e) => setForm({ ...form, assicurazione_cvt: e.target.value })} placeholder="es. Sì / importo / note" /></div>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Note iniziali (pubbliche)</Label>
                  <Textarea data-testid="new-practice-nota" value={form.note_iniziale} onChange={(e) => setForm({ ...form, note_iniziale: e.target.value })} placeholder="Descrizione o note visibili a tutti…" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button data-testid="new-practice-submit" onClick={create}>Crea Pratica</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="practice-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per numero, titolo, tipo…" className="pl-9" />
        </div>
        <MultiSelect value={statusFilter} onValueChange={setStatusFilter} className="w-48" testid="practice-status-filter"
          allLabel="Tutti gli stati" options={statuses.map((s) => ({ value: s, label: s }))} />
        <MultiSelect value={canaleFilter} onValueChange={setCanaleFilter} className="w-44" testid="practice-canale-filter"
          allLabel="Tutti i canali" options={["Convenzionato", "Diretto", "Campagna", "Altro"].map((c) => ({ value: c, label: c }))} />
        <MultiSelect value={prodottoFilter} onValueChange={setProdottoFilter} className="w-52" testid="practice-prodotto-filter"
          allLabel="Tutti i prodotti" options={products.map((p) => ({ value: p, label: p }))} />
        {isInternal(user?.role) && (
          <MultiSelect value={convFilter} onValueChange={setConvFilter} className="w-48" testid="practice-convenzionato-filter"
            allLabel="Tutti i convenzionati" searchPlaceholder="Cerca convenzionato…"
            options={owners.filter((o) => o.role === "convenzionato").map((o) => ({ value: o.id, label: o.name }))} />
        )}
        {isInternal(user?.role) && (
          <MultiSelect value={collabFilter} onValueChange={setCollabFilter} className="w-48" testid="practice-collaboratore-filter"
            allLabel="Tutti i collaboratori" searchPlaceholder="Cerca collaboratore…"
            options={owners.filter((o) => o.role === "collaboratore").map((o) => ({ value: o.id, label: o.name }))} />
        )}
        {isInternal(user?.role) && (
          <MultiSelect value={segnFilter} onValueChange={setSegnFilter} className="w-48" testid="practice-segnalatore-filter"
            allLabel="Tutti i segnalatori" searchPlaceholder="Cerca segnalatore…"
            options={owners.filter((o) => o.role === "segnalatore").map((o) => ({ value: o.id, label: o.name }))} />
        )}
        {isInternal(user?.role) && (
          <MultiSelect value={sedeFilter} onValueChange={setSedeFilter} className="w-44" testid="practice-sede-filter"
            allLabel="Tutte le sedi" options={sedi.map((s) => ({ value: s.id, label: s.nome }))} />
        )}
        <div className="flex items-center gap-1.5">
          <Input type="date" data-testid="practice-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" title="Da" />
          <span className="text-muted-foreground text-sm">→</span>
          <Input type="date" data-testid="practice-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" title="A" />
        </div>
        {isInternal(user?.role) && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="show-hidden-toggle">
            <Checkbox checked={showHidden} onCheckedChange={(v) => setShowHidden(!!v)} data-testid="show-hidden-checkbox" />
            Mostra nascoste (stati chiusi)
          </label>
        )}
        <Button variant="outline" data-testid="practice-clear-filters" onClick={() => { setQ(""); setStatusFilter("all"); setOwnerFilter("all"); setConvFilter("all"); setCollabFilter("all"); setSegnFilter("all"); setSedeFilter("all"); setCanaleFilter("all"); setProdottoFilter("all"); setDateFrom(""); setDateTo(""); setShowHidden(false); }}>
          <RotateCcw className="h-4 w-4 mr-2" />Resetta ricerca
        </Button>
      </div>

      <StatusChips counts={statusCounts} active={statusFilter} onPick={(s) => { const cur = statusFilter === "all" ? [] : statusFilter.split(","); const nx = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]; setStatusFilter(nx.length ? nx.join(",") : "all"); }} />
      {isInternal(user?.role) && <ReteLegend />}


      <Card className="overflow-hidden">
        {isAdmin && sel.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-rose-50 dark:bg-rose-950/30 border-b border-rose-200 dark:border-rose-900 flex-wrap" data-testid="practice-bulk-bar">
            <span className="text-sm font-medium">{sel.length} pratiche selezionate</span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Select value={bulkStatus || undefined} onValueChange={setBulkStatus}>
                <SelectTrigger className="h-8 w-52 bg-background" data-testid="practice-bulk-status-select"><SelectValue placeholder="Cambia stato in…" /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" data-testid="practice-bulk-status-apply" onClick={bulkChangeStatus}>Applica stato</Button>
            </div>
            <Button variant="destructive" size="sm" data-testid="practice-bulk-delete" onClick={bulkDelete}><Trash2 className="h-4 w-4 mr-2" />Elimina selezionate</Button>
            <Button variant="ghost" size="sm" data-testid="practice-bulk-cancel" onClick={() => setSel([])}>Annulla</Button>
          </div>
        )}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground select-none">
                {isAdmin && <th className="px-3 py-3 w-8"><input type="checkbox" className="accent-primary" data-testid="practice-select-all" checked={paged.length > 0 && paged.every((p) => sel.includes(p.id))} onChange={(e) => { const pids = paged.map((p) => p.id); setSel((s) => e.target.checked ? [...new Set([...s, ...pids])] : s.filter((x) => !pids.includes(x))); }} /></th>}
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-numero" onClick={() => toggleSort("numero")}>ID{arrow("numero")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-numero_istituto" onClick={() => toggleSort("numero_istituto")}>N. Pratica Istituto{arrow("numero_istituto")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-created_at" onClick={() => toggleSort("created_at")}>Data{arrow("created_at")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-cliente" onClick={() => toggleSort("cliente")}>Nome Cliente{arrow("cliente")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-rete" onClick={() => toggleSort("rete")}>Rete Vendita{arrow("rete")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-tipo" onClick={() => toggleSort("tipo_finanziamento")}>Prodotto{arrow("tipo_finanziamento")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-sede" onClick={() => toggleSort("sede_name")}>Sede{arrow("sede_name")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-importo" onClick={() => toggleSort("importo")}>Importo{arrow("importo")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-status" onClick={() => toggleSort("status")}>Stato{arrow("status")}</th>
                {isInternal(user?.role) && <th className="px-4 py-3 font-semibold cursor-pointer hover:text-foreground" data-testid="sort-assigned" onClick={() => toggleSort("assigned_name")}>In Carico a{arrow("assigned_name")}</th>}
                <th className="px-4 py-3 font-semibold text-center">Doc/Note</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => (
                <tr key={p.id} data-testid={`practice-row-${p.id}`} onClick={() => navigate(`/pratiche/${p.id}`)}
                    className="border-b border-border last:border-0 hover:bg-accent/50 cursor-pointer transition-colors">
                  {isAdmin && <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" className="accent-primary" data-testid={`practice-select-${p.id}`} checked={sel.includes(p.id)} onChange={() => toggleSel(p.id)} /></td>}
                  <td className="px-4 py-3 font-mono text-xs font-medium text-primary">{p.numero}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{p.numero_istituto || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{fmtDate(p.created_at)}</td>
                  <td className="px-4 py-3 font-medium">
                    {p.anagrafiche && p.anagrafiche.length
                      ? <>{anagName(p.anagrafiche[0])}{p.anagrafiche.length > 1 && <span className="text-xs text-muted-foreground"> +{p.anagrafiche.length - 1}</span>}</>
                      : p.titolo}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {(p.convenzionato_name || p.collaboratore_name || p.segnalatore_name) ? (
                      <div className="space-y-1.5">
                        <ReteRow label="Convenzionato" name={p.convenzionato_name} />
                        <ReteRow label="Commerciale" name={p.collaboratore_name} />
                        <ReteRow label="Segnalatore" name={p.segnalatore_name} />
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{p.tipo_finanziamento}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.sede_name || "—"}</td>
                  <td className="px-4 py-3 font-semibold">{fmtEuro(p.importo)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={p.status} id={p.id} />
                    {p.status === "Liquidata" && (
                      isInternal(user?.role) ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="text-[11px] text-muted-foreground mt-1 whitespace-nowrap hover:text-primary hover:underline" data-testid={`liquidation-date-${p.id}`} onClick={(e) => e.stopPropagation()}>
                              {fmtDate(liqOf(p)) || "Imposta data"}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-3" onClick={(e) => e.stopPropagation()}>
                            <Label className="text-xs">Data liquidazione</Label>
                            <Input type="date" className="mt-1" data-testid={`liquidation-date-input-${p.id}`}
                              defaultValue={(liqOf(p) || "").slice(0, 10)}
                              onChange={(e) => saveLiquidation(p.id, e.target.value)} />
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <div className="text-[11px] text-muted-foreground mt-1 whitespace-nowrap" data-testid={`liquidation-date-${p.id}`}>
                          {fmtDate(liqOf(p))}
                        </div>
                      )
                    )}
                  </td>
                  {isInternal(user?.role) && <td className="px-4 py-3 text-muted-foreground">{p.assigned_name || "—"}</td>}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" />{p.doc_count}</span>
                      <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{p.note_count}</span>
                    </div>
                  </td>
                </tr>
              ))}
              {practices.length === 0 && (
                <tr><td colSpan={isAdmin ? 12 : 11} className="px-4 py-16 text-center text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />Nessuna pratica trovata
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-border" data-testid="practices-mobile-list">
          {paged.map((p) => (
            <div key={p.id} data-testid={`practice-card-${p.id}`} onClick={() => navigate(`/pratiche/${p.id}`)}
                 className="p-4 hover:bg-accent/50 cursor-pointer transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex items-start gap-2">
                  {isAdmin && <input type="checkbox" className="accent-primary mt-1" data-testid={`practice-select-m-${p.id}`} checked={sel.includes(p.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(p.id)} />}
                  <div className="min-w-0">
                    <div className="font-mono text-xs font-medium text-primary">{p.numero}</div>
                    <div className="font-medium break-words">
                      {p.anagrafiche && p.anagrafiche.length
                        ? <>{anagName(p.anagrafiche[0])}{p.anagrafiche.length > 1 && <span className="text-xs text-muted-foreground"> +{p.anagrafiche.length - 1}</span>}</>
                        : p.titolo}
                    </div>
                  </div>
                </div>
                <StatusBadge status={p.status} id={p.id} />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <div><span className="font-medium text-foreground">Data:</span> {fmtDate(p.created_at)}</div>
                <div><span className="font-medium text-foreground">Importo:</span> {fmtEuro(p.importo)}</div>
                {p.numero_istituto && <div><span className="font-medium text-foreground">N. Istituto:</span> {p.numero_istituto}</div>}
                <div><span className="font-medium text-foreground">Prodotto:</span> {p.tipo_finanziamento}</div>
                {p.sede_name && <div><span className="font-medium text-foreground">Sede:</span> {p.sede_name}</div>}
                {isInternal(user?.role) && <div><span className="font-medium text-foreground">In carico:</span> {p.assigned_name || "—"}</div>}
              </div>
              {(p.convenzionato_name || p.collaboratore_name || p.segnalatore_name) && (
                <div className="mt-2 space-y-1.5">
                  <ReteRow label="Convenzionato" name={p.convenzionato_name} />
                  <ReteRow label="Commerciale" name={p.collaboratore_name} />
                  <ReteRow label="Segnalatore" name={p.segnalatore_name} />
                </div>
              )}
              {p.status === "Liquidata" && (
                <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`liquidation-date-m-${p.id}`}>
                  Liquidazione: {fmtDate(liqOf(p)) || "—"}
                </div>
              )}
              <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" />{p.doc_count}</span>
                <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{p.note_count}</span>
              </div>
            </div>
          ))}
          {practices.length === 0 && (
            <div className="px-4 py-16 text-center text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />Nessuna pratica trovata
            </div>
          )}
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border" data-testid="practices-pagination">
            <span className="text-sm text-muted-foreground">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} di {total}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="practices-prev">Prec</Button>
              <span className="px-2 text-sm tabular-nums">{page}/{pageCount}</span>
              <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} data-testid="practices-next">Succ</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


const RETE_DOT = { Convenzionato: "bg-indigo-500", Commerciale: "bg-amber-500", Segnalatore: "bg-emerald-500" };

function ReteRow({ label, name }) {
  if (!name) return null;
  return (
    <div className="flex items-center gap-2 leading-tight" title={label} data-testid={`rete-${label.toLowerCase()}`}>
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${RETE_DOT[label]}`} />
      <span className={`text-xs text-foreground ${label === "Convenzionato" ? "font-semibold" : "font-normal"}`}>{name}</span>
    </div>
  );
}

function ReteLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="rete-legend">
      <span className="uppercase tracking-wider text-[10px]">Rete vendita:</span>
      {Object.entries(RETE_DOT).map(([k, c]) => (
        <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${c}`} />{k}</span>
      ))}
    </div>
  );
}

function StatusChips({ counts, active, onPick }) {
  const names = Object.keys(STATUS_META).filter((s) => !STATUS_META[s].closed);
  if (!names.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="status-chips">
      {names.map((s) => {
        const m = STATUS_META[s];
        const on = active !== "all" && active.split(",").includes(s);
        return (
          <button key={s} type="button" onClick={() => onPick(s)} data-testid={`status-chip-${s.replace(/\s+/g, "-").toLowerCase()}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-[transform,box-shadow] hover:-translate-y-px ${m.bg} ${m.text} ${m.border} ${on ? "ring-2 ring-offset-1 ring-current shadow" : active !== "all" ? "opacity-60" : ""}`}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: m.dot }} />
            {s}
            <span className="rounded-full bg-white/70 dark:bg-black/30 px-1.5 tabular-nums">{counts[s] || 0}</span>
          </button>
        );
      })}
    </div>
  );
}
